/** @file Field check for a finite number with optional bounds and integer rule. */
import { failField } from '@zilo/pose3d-format/validate/fields/failField';
import type { FieldCheck } from '@zilo/pose3d-format/validate/fields/fieldCheck';

/** Bounds: `min`/`max` inclusive, `above`/`below` exclusive. */
export type NumberRule = { int?: boolean; min?: number; max?: number; above?: number; below?: number };

function expected(rule: NumberRule): string {
  const parts = [rule.int === true ? 'integer' : 'finite number'];
  if (rule.min !== undefined) parts.push(`>= ${rule.min}`);
  if (rule.above !== undefined) parts.push(`> ${rule.above}`);
  if (rule.max !== undefined) parts.push(`<= ${rule.max}`);
  if (rule.below !== undefined) parts.push(`< ${rule.below}`);
  return parts.join(' ');
}

function fits(v: number, rule: NumberRule): boolean {
  return (rule.int !== true || Number.isInteger(v)) && (rule.min === undefined || v >= rule.min)
    && (rule.max === undefined || v <= rule.max) && (rule.above === undefined || v > rule.above)
    && (rule.below === undefined || v < rule.below);
}

/** Check for a finite number obeying `rule`. */
export function checkNumber(rule: NumberRule = {}): FieldCheck {
  return (value, path) => {
    if (typeof value !== 'number' || !Number.isFinite(value) || !fits(value, rule)) failField(path, expected(rule), value);
  };
}
